/**
 * House management service
 * TODO(aav): remove to unit_management.ts
 * Covers unit management, sub-unit management, device sharing/transfer, and account preferences.
 * Docs: docs/units/api_multi_unit_endpoints.md
 */

import qs from 'qs';

import { real_id } from 'src/helpers/interface_helpers';
import { cloud, postReq } from '../http';
import { HttpClient } from '../utils/http-client';
export { addUnit } from './v2_users_auth';
export type { SubUnitData, SubUnitMap, SubUnitMeta, SubUnitRights } from 'src/types/SubUnit';

import type { SubUnitMap, SubUnitMeta, SubUnitRights } from 'src/types/SubUnit';
import type { GetUnitsInfoResponse } from 'src/types/responses/getUnitsInfoResponse';

// ---------------------------------------------------------------------------
// Unit listing (multi-home picker)
// ---------------------------------------------------------------------------

/**
 * Fetch all owned + shared homes (with `premium` status and avatar URL)
 * for the current bearer session, without switching the JWT.
 *
 * Endpoint: POST /user/get_units_info
 * Auth: main and sub sessions
 *
 * Optionally accepts another account context (`token` + `userApiUrl`) to query
 * `/user/get_units_info` for that account directly, without replacing the active
 * app session token. Used by the convert-account-to-home wizard for single-home
 * source accounts whose login response does not carry a home name.
 *
 * @returns Owned and shared units arrays. `avatar` is `null` when not set.
 */
export const getUnitsInfo = async (params?: {
	token?: string;
	userApiUrl?: string;
}): Promise<GetUnitsInfoResponse> => {
	if (params?.token && params?.userApiUrl) {
		const base = params.userApiUrl.endsWith('/') ? params.userApiUrl : `${params.userApiUrl}/`;
		const response = await cloud.post(`${base}user/get_units_info`, '' as unknown as object, {
			headers: {
				Authorization: `Bearer ${params.token}`,
				'Content-Type': 'application/x-www-form-urlencoded',
			},
			timeout: 60000,
		});
		return response.data as GetUnitsInfoResponse;
	}

	const response = (await postReq(
		'/user/get_units_info',
		{},
		60000,
	)) as unknown as GetUnitsInfoResponse;
	return response;
};

// ---------------------------------------------------------------------------
// Unit management
// ---------------------------------------------------------------------------

/**
 * Delete one of the account's own units (homes).
 * Cannot delete the last unit on an account.
 *
 * @param unitId - The integer unit id to delete
 */
export const deleteUnit = async (unitId: number): Promise<{ isok: boolean; [key: string]: any }> => {
	const response = await cloud.post(`/user/delete_unit?unit_id=${unitId}`);
	return response.data;
};

/** Payload nested under `data` from `POST /device/check_unit_devices`. */
export type CheckUnitDevicesData = {
	unit_id?: number;
	/** Devices owned by this unit (role 1). */
	owned?: number;
	/** Devices shared to this unit (other non-empty roles). */
	shared?: number;
};

/** Response from `POST /device/check_unit_devices` (counts live under `data`). */
export type CheckUnitDevicesResponse = {
	isok: boolean;
	data?: CheckUnitDevicesData;
	/** Flat shape fallback (if API ever returns counts at root). */
	owned?: number;
	shared?: number;
	[key: string]: unknown;
};

/**
 * Returns owned vs shared-to-unit device counts for a home.
 * If `unitId` is omitted, the JWT `user_id` (current unit) is used.
 * `unit_id` must belong to the caller.
 */
export const checkUnitDevices = async (unitId?: number): Promise<CheckUnitDevicesResponse> => {
	const params = new URLSearchParams();
	if (unitId !== undefined) {
		params.set('unit_id', String(unitId));
	}

	const response = await cloud.post(
		'/device/check_unit_devices',
		params.toString() as unknown as object,
		{
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
		},
	);
	return response.data as CheckUnitDevicesResponse;
};

/**
 * Rename a unit (owner) or update the sub's display name (sub session).
 *
 * Owner session: pass `unitId` + `name`. The new house name is written to
 * cluster `u:{unitId}.name`. No `USR` updates.
 *
 * Sub session: pass `name` only (omit `unitId`). The owner's `sub_*` meta
 * `name` is updated — i.e. the sub's own display name within that owner's
 * unit. No `USR` updates beyond the `sub_*` meta entry.
 *
 * @param unitId - Integer unit id (owner sessions only; omit when called as a sub unit)
 * @param name   - The new name
 */
export const setUnitName = async (
	unitId: number | null | undefined,
	name: string,
): Promise<{ isok: boolean; [key: string]: any }> => {
	const body: Record<string, unknown> = { name };
	if (unitId != null) {
		body.unit_id = unitId;
	}

	const response = await postReq('/user/set_unit_name', body, 10000, null, false, false, {
		'Content-Type': 'x-www-form-urlencoded',
	});

	return response;
};

/**
 * Set the avatar for the current (active) home.
 *
 * Calls `POST /user/set_avatar`, which writes the home avatar
 * (`p:{email}.avatar`) for the active unit session.
 *
 * @param avatar - Home avatar payload (`{ text?, image?, backgroundColor? }`)
 */
export const setHomeAvatar = async (avatar: unknown): Promise<{ isok: boolean; [key: string]: any }> => {
	const response = await postReq('/user/set_avatar', { avatar: JSON.stringify(avatar) });
	return response;
};

/**
 * Transfer a unit from an *existing* source account to the current account.
 * The caller provides the source account's credentials; the unit is moved to
 * the current (target) account.
 *
 * @param unitId   - Unit id to transfer
 * @param email    - Source account email
 * @param password - Source account password (base64-encoded)
 */
export const transferUnit = async (
	unitId: number,
	email: string,
	password: string,
): Promise<{ isok: boolean; [key: string]: any }> => {
	const body = { unit_id: unitId, email, password };

	const response = await postReq('/user/transfer_unit', body, 10000, null, false, false, {
		'Content-Type': 'x-www-form-urlencoded',
	});

	return response;
};

/**
 * Receive (adopt) a unit from an existing owner account.
 * The caller is the recipient; owner credentials are required for verification.
 *
 * @param unitId        - Unit id to receive
 * @param ownerEmail    - Owner account email
 * @param ownerPassword - Owner account password (base64-encoded)
 */
export const receiveUnit = async (
	unitId: number,
	ownerEmail: string,
	ownerPassword: string,
): Promise<{ isok: boolean; [key: string]: any }> => {
	const body = { unit_id: unitId, owner_email: ownerEmail, owner_password: ownerPassword };

	const response = await postReq('/user/receive_unit', body, 10000, null, false, false, {
		'Content-Type': 'x-www-form-urlencoded',
	});

	return response;
};

/**
 * Transfer a unit from the current account to a brand-new account.
 * Fails if the target email is already registered.
 *
 * @param unitId   - Unit id to transfer out
 * @param email    - New account email
 * @param password - New account password (base64-encoded)
 */
export const transferUnitToNewAccount = async (
	unitId: number,
	email: string,
	password: string,
): Promise<{ isok: boolean; [key: string]: any }> => {
	const body = { unit_id: unitId, email, password };

	const response = await postReq(
		'/user/transfer_unit_to_new_account',
		body,
		10000,
		null,
		false,
		false,
		{
			'Content-Type': 'x-www-form-urlencoded',
		},
	);

	return response;
};

/**
 * Offer a home to a recipient by email (async transfer, no dual-login).
 *
 * Endpoint: POST /user/offer_unit_transfer
 * Auth: Bearer (owner).
 *
 * The backend routes automatically: if the email already has a registered
 * account the transfer becomes a pending offer the recipient confirms in-app;
 * otherwise it behaves like `transfer_unit_to_new_account` (email registration
 * link). The home stays with the owner until the recipient accepts (or the
 * offer expires after `UNIT_TRANSFER_PENDING_TTL`).
 *
 * @param unitId - Unit id to offer
 * @param email  - Recipient email
 */
export const offerUnitTransfer = async (
	unitId: number,
	email: string,
): Promise<{ isok: boolean; [key: string]: any }> => {
	const body = { unit_id: unitId, email };

	const response = await postReq('/user/offer_unit_transfer', body, 10000, null, false, false, {
		'Content-Type': 'x-www-form-urlencoded',
	});

	return response;
};

/**
 * Fetch the current account's pending home-ownership transfers.
 *
 * Endpoint: POST /user/get_pending_unit_transfers
 * Auth: Bearer.
 *
 * Returns `{ outgoing, incoming }` — `outgoing` when this account is the
 * sender, `incoming` when it is the recipient (both can be present).
 */
export const getPendingUnitTransfers = async (): Promise<{
	isok: boolean;
	[key: string]: any;
}> => {
	const response = await postReq('/user/get_pending_unit_transfers', {}, 10000, null, false, false, {
		'Content-Type': 'x-www-form-urlencoded',
	});

	return response;
};

/**
 * Cancel a pending outgoing home-ownership transfer (sender action).
 *
 * Endpoint: POST /user/cancel_unit_transfer
 * Auth: Bearer (owner).
 */
export const cancelUnitTransfer = async (
	unitId: number,
): Promise<{ isok: boolean; [key: string]: any }> => {
	const response = await postReq(
		'/user/cancel_unit_transfer',
		{ unit_id: unitId },
		10000,
		null,
		false,
		false,
		{ 'Content-Type': 'x-www-form-urlencoded' },
	);

	return response;
};

/**
 * Accept a pending incoming home-ownership transfer (recipient action).
 *
 * Endpoint: POST /user/accept_unit_transfer
 * Auth: Bearer (recipient).
 */
export const acceptUnitTransfer = async (
	unitId: number,
): Promise<{ isok: boolean; [key: string]: any }> => {
	const response = await postReq(
		'/user/accept_unit_transfer',
		{ unit_id: unitId },
		10000,
		null,
		false,
		false,
		{ 'Content-Type': 'x-www-form-urlencoded' },
	);

	return response;
};

/**
 * Existing-account path: decline a pending incoming transfer while logged in.
 *
 * POST /user/decline_unit_transfer
 * Auth: Bearer (logged-in recipient session).
 * Parameters: unit_id (required).
 */
export const declineUnitTransfer = async (
	unitId: number,
): Promise<{ isok: boolean; [key: string]: any }> => {
	const response = await postReq(
		'/user/decline_unit_transfer',
		{ unit_id: unitId },
		10000,
		null,
		false,
		false,
		{ 'Content-Type': 'x-www-form-urlencoded' },
	);

	return response;
};

/**
 * Same host as `postReq` / subaccount auth (`VITE_CLOUD_URL`), not `VITE_API_URL`
 * or the token `user_api_url` shard — those reject `unitxfer` with `invalid_token`.
 */
const getAuthInviteBaseUrl = (): string =>
	String(import.meta.env.VITE_CLOUD_URL || '').replace(/\/+$/, '');

/** Temp client for invite-token auth — avoids the logged-in session client. */
const createUnitTransferAuthClient = (token: string): HttpClient => {
	const client = new HttpClient(getAuthInviteBaseUrl());
	client.setAuthToken(token);
	return client;
};

/**
 * New-account path: set password and complete the pending home transfer.
 *
 * POST /auth/complete_unit_transfer_registration
 * Auth: Bearer `unitxfer` token from the offer email.
 */
export const completeUnitTransferRegistration = async (
	token: string,
	email: string,
	password: string,
): Promise<{ isok: boolean; [key: string]: any }> => {
	try {
		const res = await createUnitTransferAuthClient(token).post(
			'/auth/complete_unit_transfer_registration',
			qs.stringify({ email, password: window.btoa(password).toString() }) as unknown as object,
			{ headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
		);
		const data = res?.data ?? {};
		const isok = Boolean(data?.isok ?? data?.isOk);
		if (!isok) {
			return { isok: false, errors: data?.errors ?? data };
		}
		return { ...data, isok: true };
	} catch (error) {
		console.error('[completeUnitTransferRegistration] failed', error);
		return { isok: false, errors: error?.response?.data?.errors ?? error?.response?.data };
	}
};

/**
 * New-account path: decline via email link (no login).
 *
 * POST /auth/decline_unit_transfer
 * Auth: Bearer `unitxfer` token from the offer email.
 * Parameters: unit_id (required), email (required).
 *
 * Uses a temp HttpClient (not the session client) so a 401 does not trigger
 * session refresh / "Session expired". Paths are in NON_RETRYABLE_URLS.
 */
export const declineUnitTransferAuth = async (
	token: string,
	email: string,
	unitId: number,
): Promise<{ isok: boolean; [key: string]: any }> => {
	try {
		const res = await createUnitTransferAuthClient(token).post(
			'/auth/decline_unit_transfer',
			qs.stringify({ email, unit_id: unitId }) as unknown as object,
			{ headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
		);
		const data = res?.data ?? {};
		const isok = Boolean(data?.isok ?? data?.isOk);
		if (!isok) {
			console.error('[declineUnitTransferAuth] failed', data);
			return { isok: false, errors: data?.errors ?? data };
		}
		return { ...data, isok: true };
	} catch (error) {
		console.error('[declineUnitTransferAuth] failed', error);
		return { isok: false, errors: error?.response?.data?.errors ?? error?.response?.data };
	}
};

// ---------------------------------------------------------------------------
// Sub-unit management
// ---------------------------------------------------------------------------

/**
 * Fetch all sub units for the current unit.
 * Requires main account auth (not callable as a sub unit).
 */
export const getSubUnits = async (): Promise<{
	isok: boolean;
	data?: SubUnitMap;
	[key: string]: any;
}> => {
	const response = await cloud.get('/user/get_sub_units');
	return response;
};

/**
 * Invite a new sub unit (shared user) to the current unit.
 *
 * @param meta   - Sub unit metadata (email, lang, tz, nperm, name are required)
 * @param rights - Optional permission arrays; defaults to full access [['*'],['*'],['*'],['*'],['*']]
 */
export const addSubUnit = async (
	meta: SubUnitMeta,
	rights?: SubUnitRights,
): Promise<{ isok: boolean; sub?: string; [key: string]: any }> => {
	const body: Record<string, any> = {
		meta: JSON.stringify(meta),
	};

	if (rights) {
		body.rights = JSON.stringify(rights);
	}
	///user/add_sub_unit
	const response = await postReq('/user/add_sub_unit', body, 10000, null, false, false, {
		'Content-Type': 'x-www-form-urlencoded',
	});

	return response;
};

/**
 * Remove a sub unit.
 * - Owner: pass `sub` to remove a specific shared user.
 * - Sub user calling without `sub`: removes themselves from the unit.
 *
 * @param sub - Sub unit id (omit when the sub unit is removing themselves)
 */
export const removeSubUnit = async (sub?: string): Promise<{ isok: boolean; [key: string]: any }> => {
	const body: Record<string, any> = {};
	if (sub) body.sub = sub;

	const response = await postReq('/user/remove_sub_unit', body, 10000, null, false, false, {
		'Content-Type': 'x-www-form-urlencoded',
	});

	return response;
};

/**
 * Renew an expired sub unit invitation without creating a new sub.
 *
 * @param sub - Sub unit id to renew
 */
export const renewSubUnit = async (sub: string): Promise<{ isok: boolean; [key: string]: any }> => {
	const response = await cloud.post('/user/renew_sub_unit', { sub });
	return response.data;
};

/**
 * Replace a sub unit's full rights array in one call.
 *
 * @param sub    - Sub unit id
 * @param rights - Five permission arrays (control, settings, scenes run, scenes enable, rooms)
 */
export const changeSubUnitRights = async (
	sub: string,
	rights: SubUnitRights,
): Promise<{ isok: boolean; [key: string]: any }> => {
	const response = await postReq(
		'/user/change_sub_unit_rights',
		{
			sub,
			rights: JSON.stringify(rights),
		},
		10000,
		null,
		false,
		false,
		{
			'Content-Type': 'x-www-form-urlencoded',
		},
	);
	return response;
};

/**
 * Update a single permission slot for a sub unit.
 *
 * @param sub  - Sub unit id
 * @param perm - Permission index: 0 = control, 1 = settings, 2 = scenes run, 3 = scenes enable, 4 = rooms
 * @param ids  - Device/scene/room ids to allow, or ['*'] for all
 */
export const updateSubUnitRights = async (
	sub: string,
	perm: 0 | 1 | 2 | 3 | 4,
	ids: string[],
): Promise<{ isok: boolean; [key: string]: any }> => {
	const response = await cloud.post('/user/update_sub_unit_rights', {
		sub,
		perm,
		ids: JSON.stringify(ids),
	});
	return response.data;
};

// ---------------------------------------------------------------------------
// Device operations
// ---------------------------------------------------------------------------

/**
 * Transfer ownership of devices to another existing account.
 * The recipient's credentials must be provided; devices move to their first unit.
 *
 * @param devices  - Array of device ids to transfer
 * @param email    - Recipient account email
 * @param password - Recipient password as SHA1 hex string (same as v2 login / saved profiles — not base64)
 */
export const changeDeviceOwner = async (
	devices: string[],
	email: string,
	password: string,
): Promise<{ isok: boolean; [key: string]: any }> => {
	// API expects form body (see docs/units/multi_unit_api.postman_collection.json), not JSON.
	const params = new URLSearchParams();
	params.set('devices', JSON.stringify(devices));
	params.set('email', email);
	params.set('password', password);

	const response = await cloud.post('/device/change_owner', params.toString() as unknown as object, {
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
		},
	});
	return response.data;
};

/**
 * Move devices from the current unit to another unit of the same account.
 *
 * @param devices      - Array of device ids to move
 * @param targetUnitId - Target unit id (must belong to the same account)
 */
export const transferDevicesToUnit = async (
	devices: string[],
	targetUnitId: number,
): Promise<{ isok: boolean; [key: string]: any }> => {
	// API expects form body (see docs/units/multi_unit_api.postman_collection.json), not JSON.
	const params = new URLSearchParams();
	params.set('devices', JSON.stringify(devices));
	params.set('target_unit_id', String(targetUnitId));

	const response = await cloud.post(
		'/device/transfer_devices_to_unit',
		params.toString() as unknown as object,
		{
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
		},
	);

	return response.data;
};

/**
 * Share a device with another user's unit.
 *
 * @param id      - Device id
 * @param email   - Recipient email
 * @param channel - Device channel (e.g. 0)
 */
export const addSharedDevice = async (
	id: string,
	email: string,
	channel: number,
): Promise<{ isok: boolean; [key: string]: any }> => {
	const params = new URLSearchParams();
	params.set('id', id);
	params.set('email', email);
	params.set('channel', String(channel));

	const response = await cloud.post('/device/add_shared', params.toString() as unknown as object, {
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
		},
	});
	return response.data;
};

/**
 * Get the list of emails a device is currently shared with.
 *
 * @param id      - Device id
 * @param channel - Device channel
 */
export const getSharedDevice = async (
	id: string,
	channel: number,
): Promise<{ isok: boolean; data?: { emails?: string[]; users?: string[] }; [key: string]: any }> => {
	// HttpClient GET returns the parsed JSON body directly (not axios-style { data }).
	return (await cloud.get('/device/get_shared', { params: { id, channel } })) as {
		isok: boolean;
		data?: { emails?: string[]; users?: string[] };
		[key: string]: any;
	};
};

async function postDeleteShared(
	id: string,
	recipientEmail: string,
): Promise<{ isok: boolean; [key: string]: any }> {
	const params = new URLSearchParams();
	params.set('id', id);
	params.set('email', recipientEmail);

	const response = await cloud.post('/device/delete_shared', params.toString() as unknown as object, {
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
		},
	});
	return response.data;
}

/**
 * Revoke share via POST /device/delete_shared (requires `id` + `email`).
 * With `email`: revoke for that recipient only. Recipients leaving the share
 * must pass their own email — they cannot call GET /device/get_shared (owner-only).
 * Without `email`: GET /device/get_shared then delete_shared once per recipient.
 * Owner-only; do not use this branch for "remove from my account".
 *
 * @param id      - Device interface id
 * @param email   - Recipient email (omit to revoke every recipient returned by get_shared)
 * @param channel - Device channel for get_shared when `email` is omitted (default 0)
 */
export const deleteSharedDevice = async (
	id: string,
	email?: string,
	channel = 0,
): Promise<{ isok: boolean; [key: string]: any }> => {
	if (email) {
		return postDeleteShared(id, email);
	}

	const listRes = await getSharedDevice(real_id(id), channel);
	if (!listRes.isok) {
		return listRes;
	}

	const users =
		listRes.data?.users ?? (listRes.data as { emails?: string[] } | undefined)?.emails ?? [];

	if (users.length === 0) {
		return { isok: true };
	}

	let allOk = true;
	let last: { isok: boolean; [key: string]: any } = { isok: true };

	for (const recipientEmail of users) {
		last = await postDeleteShared(id, recipientEmail);
		if (!last.isok) {
			allOk = false;
		}
	}

	return { ...last, isok: allOk };
};

/**
 * Revoke shares for multiple devices in one call.
 *
 * @param ids - Array of interface ids to unshare
 */
export const bulkDeleteSharedDevices = async (
	ids: string[],
): Promise<{ isok: boolean; [key: string]: any }> => {
	const params = new URLSearchParams();
	params.set('ids', JSON.stringify(ids));

	const response = await cloud.post(
		'/device/bulk_delete_shared',
		params.toString() as unknown as object,
		{
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
		},
	);
	return response.data;
};

/**
 * Move a shared device from the current unit to another unit of the same account.
 *
 * @param id           - Interface id of the shared device
 * @param targetUnitId - Target unit id (must belong to the same account)
 */
export const transferSharedToUnit = async (
	id: string,
	targetUnitId: number,
): Promise<{ isok: boolean; [key: string]: any }> => {
	const params = new URLSearchParams();
	params.set('id', id);
	params.set('target_unit_id', String(targetUnitId));

	const response = await cloud.post(
		'/device/transfer_shared_to_unit',
		params.toString() as unknown as object,
		{
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
		},
	);
	return response.data;
};

// ---------------------------------------------------------------------------
// Account/unit preferences
// ---------------------------------------------------------------------------

/**
 * Save a preference for the main account (applies across all units).
 * When called as a sub unit, the request is forwarded to `setSubUnits`.
 *
 * @param set - Setting key: 'temp_unit' or 'date_format'
 * @param val - Setting value: 'C'|'F'|'' for temp_unit; 'us'|'eu' for date_format
 */
export const setAccountUnits = async (
	set: 'temp_unit' | 'date_format',
	val: string,
): Promise<{ isok: boolean; [key: string]: any }> => {
	const response = await cloud.post('/user/set_account_units', { set, val });
	return response.data;
};

/**
 * Save a preference in the sub unit's own metadata.
 * Typically called when the session belongs to a sub unit.
 *
 * @param set - Setting key: 'temp_unit' or 'date_format'
 * @param val - Setting value: 'C'|'F'|'' for temp_unit; 'us'|'eu' for date_format
 */
export const setSubUnits = async (
	set: 'temp_unit' | 'date_format',
	val: string,
): Promise<{ isok: boolean; [key: string]: any }> => {
	const response = await cloud.post('/user/set_sub_units', { set, val });
	return response.data;
};
